<template>
  <div class="discovery-options__header">
    <h6 class="title-small-bold">Thématiques et sous-thématiques</h6>

    <SelectSearchBar
      v-model.trim="searchField"
      @close-menu="$emit('close-menu')"
    />
  </div>
  <!-- Option to select -->
  <ul class="discovery-options" role="menuitemcheckbox">
    <template v-for="(item, index) in searchedThematics" :key="index">
      <CollapseSection ref="thematicsCollapse" :initial-active="false">
        <template #title>
          <span class="discovery-options__checkbox">
            <i
              :class="[
                isThematicActive(item._id) ||
                isThematicPartiallyActive(item._id)
                  ? 'fa-solid'
                  : 'fa-regular fa-square',
                isThematicActive(item._id)
                  ? 'fa-square-check'
                  : 'fa-square-minus',
              ]"
              @click.stop="() => toggleThematic(item._id)"
            ></i>

            {{ item.name }}
          </span>
        </template>

        <template #content>
          <li
            v-for="(subitem, subindex) in item.subthematics"
            :key="subindex"
            v-focus
            class="discovery-options__item"
            tabindex="0"
            @click.stop="() => toggleSubthematic(item._id, subitem._id)"
            @keydown.enter.stop="() => toggleSubthematic(item._id, subitem._id)"
          >
            <span class="discovery-options__checkbox">
              <i
                :class="[
                  isSubthematicActive(subitem._id) || isThematicActive(item._id)
                    ? 'fa-solid fa-square-check'
                    : 'fa-regular fa-square',
                ]"
              />
            </span>
            <h4 class="title-medium-semibold">
              {{ subitem.name }}
              <span v-if="subitem.description?.length > 0">
                {{ subitem.description }}
              </span>
            </h4>
          </li>
        </template>
      </CollapseSection>
    </template>
    <p v-if="searchField && !searchedThematics.length" class="no-result">
      Aucune thématique ou sous-thématique ne correspond à cette recherche.
    </p>
  </ul>

  <div class="discovery-options__footer">
    <button
      v-focus
      type="button"
      class="btn-secondary btn-reset discovery-options__footer__button"
      @click="$emit('reset')"
      @keydown.tab="emitCloseMenu"
    >
      <i class="fa-solid fa-arrow-rotate-left"></i>
      <span class="label">Réinitialiser</span>
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import SelectSearchBar from "@follaw-fr/components/reusable/atoms/SelectSearchBar.vue";
import CollapseSection from "@follaw-fr/components/agenda/atoms/CollapseSection.vue";
import { searchNormalized } from "@follaw-fr/utils/helper.utils";

const props = defineProps({
  thematics: {
    type: Array,
    default: () => [],
  },
  activeThematics: {
    type: Array,
    default: () => [],
  },
  activeSubthematics: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "addThematic",
  "addSubthematic",
  "removeThematic",
  "removeSubthematic",
  "reset",
  "close-menu",
]);

const searchField = ref("");
const thematicsCollapse = ref([]);

const searchedThematics = computed(() => {
  // Construct and return a new array of thematics,
  // where only the items matching the search will be returned
  return props.thematics
    .map((thematic) => {
      const searchedSubthematics = thematic.subthematics.filter(
        (subthematic) =>
          searchNormalized(thematic.name, searchField.value) ||
          searchNormalized(subthematic.name, searchField.value)
      );
      if (searchedSubthematics.length > 0) {
        return { ...thematic, subthematics: searchedSubthematics };
      }
    })
    .filter((n) => n !== undefined); // Remove undefined items
});

const isThematicActive = (themId) => {
  return props.activeThematics.map(({ _id }) => _id).includes(themId);
};

const isThematicPartiallyActive = (themId) => {
  const them = props.thematics.find(({ _id }) => _id === themId);
  return them.subthematics.some((subT) => isSubthematicActive(subT._id));
};

const isSubthematicActive = (subthemId) => {
  return props.activeSubthematics.map(({ _id }) => _id).includes(subthemId);
};

const toggleThematic = (themId) => {
  const them = props.thematics.find(({ _id }) => _id === themId);
  if (isThematicActive(themId)) {
    emit("removeThematic", { item: themId });
  } else {
    emit("addThematic", { item: them });

    // Special case: if some subthematics were already activated,
    // deactivate them because the thematic will be a catch-all
    const previouslyActiveSubT = them.subthematics.filter((_s) =>
      isSubthematicActive(_s._id)
    );
    for (const subT of previouslyActiveSubT) {
      emit("removeSubthematic", { item: subT._id });
    }
  }
};

const toggleSubthematic = async (themId, subthemId) => {
  const them = props.thematics.find(({ _id }) => _id === themId);
  const subthem = them.subthematics.find(({ _id }) => _id === subthemId);

  if (isThematicActive(themId)) {
    // Special case: if the thematic was activated, deactivating
    // a subthematic will deactivate the thematic and
    // activate all other subthematics

    emit("removeThematic", { item: themId });

    for (const otherSubT of them.subthematics) {
      if (subthemId === otherSubT._id) {
        emit("removeSubthematic", { item: subthemId });
      } else {
        emit("addSubthematic", { item: otherSubT });
      }
    }
  } else {
    if (isSubthematicActive(subthemId)) {
      emit("removeSubthematic", { item: subthemId });
    } else {
      emit("addSubthematic", { item: subthem });

      // Do an await to make sure that the emit has been processed
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Special case: if all subthematics are activated, activate the thematic instead
      const allSubTActive = them.subthematics.every((_s) =>
        isSubthematicActive(_s._id)
      );
      if (allSubTActive) {
        toggleThematic(them._id);
      }
    }
  }
};

// Automatically open thematics when the search matches one of their subthematic
// or close them all when search is empty
watch([searchField], () => {
  const isSearchEmpty = searchField.value.length === 0;

  thematicsCollapse.value.forEach((tc) =>
    isSearchEmpty ? tc.close() : tc.open()
  );
});
</script>

<style lang="scss" scoped>
.discovery-options {
  list-style: none;
  padding: 4px 16px;
  margin: 0;
  overflow: scroll;
  gap: 8px;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px;
  }

  &__item {
    display: flex;
    gap: 8px;
    padding: 0 4px;
    cursor: pointer;
  }

  &__checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding-right: 16px;

    &__button {
      gap: 4px;
      display: flex;
      align-items: center;
    }
  }
}
</style>
